import React from "react";
import axios from "axios";
import { Mail, Send, Check } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function NewsletterCTA({ source = "blog", variant = "card" }) {
  const [email, setEmail] = React.useState("");
  const [status, setStatus] = React.useState("idle"); // idle | loading | success | error
  const [errorMsg, setErrorMsg] = React.useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (!email || !email.includes("@")) {
      setStatus("error");
      setErrorMsg("Indtast en gyldig email");
      return;
    }
    setStatus("loading");
    setErrorMsg("");
    try {
      await axios.post(`${API}/newsletter/subscribe`, { email: email.trim().toLowerCase(), source });
      setStatus("success");
    } catch (err) {
      setStatus("error");
      setErrorMsg(err?.response?.data?.detail || "Noget gik galt — prøv igen senere");
    }
  };

  if (status === "success") {
    return (
      <div className="rounded-2xl border border-green-200 bg-green-50 p-6 text-center" data-testid="newsletter-success">
        <Check size={32} className="mx-auto text-green-600 mb-2" />
        <h3 className="font-display text-xl font-bold text-brand-dark">Tak — du er tilmeldt!</h3>
        <p className="mt-1 text-sm text-brand-text2">
          Vi sender dig kuratorerede guides og tips et par gange om måneden. Du kan altid afmelde dig.
        </p>
      </div>
    );
  }

  const inputClass =
    variant === "inline"
      ? "flex-1 rounded-lg border border-slate-300 bg-white px-4 py-3 text-sm focus:border-brand-red focus:outline-none focus:ring-2 focus:ring-brand-red/20"
      : "w-full rounded-lg border border-slate-300 bg-white px-4 py-3 text-sm focus:border-brand-red focus:outline-none focus:ring-2 focus:ring-brand-red/20";

  return (
    <div
      className={
        variant === "inline"
          ? "rounded-2xl border border-slate-200 bg-white p-6"
          : "rounded-2xl border border-slate-200 bg-gradient-to-br from-white to-slate-50 p-6 sm:p-8"
      }
      data-testid="newsletter-cta"
    >
      <div className="flex items-center gap-2 text-xs uppercase tracking-widest font-bold text-brand-red mb-2">
        <Mail size={14} /> Nyhedsbrev
      </div>
      <h3 className="font-display text-xl sm:text-2xl font-bold text-brand-dark">
        Få vores bedste kurer-guides direkte i indbakken
      </h3>
      <p className="mt-2 text-sm text-brand-text2">
        Ingen spam. Et par gange om måneden sender vi praktiske tips, prisindsigter og nye guides — kuretarerede til danske webshops og virksomheder.
      </p>
      <form onSubmit={submit} className={variant === "inline" ? "mt-4 flex flex-col sm:flex-row gap-2" : "mt-4 space-y-2"} data-testid="newsletter-form">
        <input
          type="email"
          placeholder="din@email.dk"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className={inputClass}
          disabled={status === "loading"}
          required
          data-testid="newsletter-email-input"
        />
        <button
          type="submit"
          disabled={status === "loading"}
          className={
            (variant === "inline" ? "" : "w-full ") +
            "inline-flex items-center justify-center gap-2 rounded-lg bg-brand-red text-white px-5 py-3 text-sm font-bold hover:bg-brand-red/90 disabled:opacity-60 transition-colors"
          }
          data-testid="newsletter-submit"
        >
          {status === "loading" ? "Tilmelder..." : "Tilmeld"}
          {status !== "loading" && <Send size={14} />}
        </button>
      </form>
      {status === "error" && (
        <div className="mt-2 text-xs text-red-600" data-testid="newsletter-error">{errorMsg}</div>
      )}
      <div className="mt-3 text-xs text-brand-text2">
        Ved at tilmelde dig accepterer du vores{" "}
        <a href="/handelsbetingelser" className="underline hover:text-brand-red">privatlivspolitik</a>.
      </div>
    </div>
  );
}
